import LoggingBase from '../../../../base/loggingbase';
import Validator from '../../../../utils/Validator';

/**
 * class EditingElementSelectionManagerExtension - manages the text selection
 * (start/end/direction) of an editing element's content-editable DOM element. It is a
 * plain delegate: the host is passed in and accessed via {@link #host}. Hosts:
 * {@link InputField}, {@link Textarea}, {@link SearchInputMenuItem}.
 */
export default class EditingElementSelectionManagerExtension extends LoggingBase {

	/**
	 * constructs a new instance
	 * @param {*} host the host editing element
	 */
	constructor( host ) {
		super( 'widgets.xtw.editing.EditingElementSelectionManagerExtension' );
		this._host = host;
	}

	/**
	 * @returns {*} the host editing element
	 */
	get host() {
		return this._host;
	}

	/**
	 * @override
	 */
	doDestroy() {
		this._host = void 0;
		super.doDestroy();
	}

	get selectionStart() {
		const contentEditableElement = this.host.contentEditableElement;
		if ( !( contentEditableElement instanceof HTMLElement ) ) {
			return 0;
		}
		const selectionStart = contentEditableElement.selectionStart;
		return Validator.isPositiveInteger( selectionStart ) ? selectionStart : 0;
	}

	set selectionStart( startValue ) {
		const contentEditableElement = this.host.contentEditableElement;
		if ( !( contentEditableElement instanceof HTMLElement ) ) {
			return;
		}
		const currentContentLength = this.host.currentContentLength;
		if ( !Validator.isPositiveInteger( currentContentLength, false ) ) {
			return;
		}
		if ( !Validator.isPositiveInteger( startValue ) ||
			startValue > currentContentLength ) {
			return;
		}
		contentEditableElement.selectionStart = startValue;
	}

	get selectionEnd() {
		const contentEditableElement = this.host.contentEditableElement;
		if ( !( contentEditableElement instanceof HTMLElement ) ) {
			return 0;
		}
		const selectionEnd = contentEditableElement.selectionEnd;
		return Validator.isPositiveInteger( selectionEnd ) ? selectionEnd : 0;
	}

	set selectionEnd( endValue ) {
		const contentEditableElement = this.host.contentEditableElement;
		if ( !( contentEditableElement instanceof HTMLElement ) ) {
			return;
		}
		const currentContentLength = this.host.currentContentLength;
		if ( !Validator.isPositiveInteger( currentContentLength, false ) ) {
			return;
		}
		if ( !Validator.isPositiveInteger( endValue ) ||
			endValue > currentContentLength ) {
			return;
		}
		contentEditableElement.selectionEnd = endValue;
	}

	get selectionDirection() {
		const contentEditableElement = this.host.contentEditableElement;
		if ( !( contentEditableElement instanceof HTMLElement ) ) {
			return "forward";
		}
		const selectionDirection = contentEditableElement.selectionDirection;
		return [ "forward", "backward", "none" ].indexOf( selectionDirection ) < 0 ?
			"forward" : selectionDirection;
	}

	set selectionDirection( direction ) {
		const contentEditableElement = this.host.contentEditableElement;
		if ( !( contentEditableElement instanceof HTMLElement ) ) {
			return;
		}
		if ( [ "forward", "backward", "none" ].indexOf( direction ) < 0 ) {
			return;
		}
		contentEditableElement.selectionDirection = direction;
	}

	setSelection( selectionStart = 0, selectionEnd = 0, selectionDirection = "forward" ) {
		this.selectionStart = selectionStart;
		this.selectionEnd = selectionEnd;
		this.selectionDirection = selectionDirection;
	}

}
